import { useCallback, useEffect, useState } from "react";
import API from "../../lib/api";
import { CalendarCheck, ShieldCheck, Clock, CheckCircle } from "@phosphor-icons/react";
import { PpmGrid } from "../../components/ppm/PpmGrid";
import { isoDaysAgo, fmtD, CELL, Pill, Spinner } from "../../components/ppm/ppmUtils";

/** Client portal — read-only planned maintenance across the client's buildings. */
export default function ClientPpmPage() {
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(26);
  const load = useCallback(() => { API.get("/client-hub/ppm", { params: { start: isoDaysAgo(28), weeks } }).then((r) => setData(r.data)).catch(() => setData({ groups: [], columns: [], summary: {} })); }, [weeks]);
  useEffect(() => { load(); }, [load]);
  const s = data?.summary || {};
  const upcoming = (data?.groups || []).flatMap((g) => g.rows.flatMap((r) => r.cells.filter((c) => ["planned", "scheduled", "in_progress", "overdue"].includes(c.state)).map((c) => ({ ...c, title: r.title, property_name: g.property_name, asset_name: r.asset_name, statutory: r.statutory }))))
    .sort((a, b) => a.due.localeCompare(b.due)).filter((c) => c.due >= new Date().toISOString().slice(0, 10)).slice(0, 8);

  return (
    <div className="space-y-5" data-testid="client-ppm-page">
      <div>
        <h1 className="text-2xl font-bold font-[Chivo] flex items-center gap-2"><CalendarCheck size={26} className="text-primary" /> Planned maintenance</h1>
        <p className="text-sm text-muted-foreground">Every scheduled maintenance visit across your buildings — what's done, what's booked and what's coming up.</p>
      </div>
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="client-ppm-stats">
          <Tile label="Schedules" value={s.active} testid="client-ppm-stat-active" />
          <Tile label="Statutory" value={s.statutory} icon={ShieldCheck} testid="client-ppm-stat-statutory" />
          <Tile label="Due this week" value={s.due_this_week} icon={Clock} testid="client-ppm-stat-week" />
          <Tile label="Completed (shown period)" value={s.completed} icon={CheckCircle} tone="text-emerald-600" testid="client-ppm-stat-completed" />
        </div>
      )}
      {upcoming.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-3" data-testid="client-ppm-upcoming">
          <p className="text-xs font-semibold mb-2">Coming up</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {upcoming.map((u) => { const c = CELL[u.state] || CELL.planned; return (
              <div key={`${u.title}-${u.due}`} className="flex items-center gap-2 text-xs rounded-md border border-border px-2 py-1.5">
                <span className={`h-2 w-2 rounded-full ${c.dot}`} /><span className="font-medium truncate">{u.title}</span><span className="text-muted-foreground truncate">{u.property_name}{u.asset_name ? ` · ${u.asset_name}` : ""}</span><span className="ml-auto text-muted-foreground whitespace-nowrap">{fmtD(u.due)}</span>{u.statutory && <ShieldCheck size={12} className="text-primary" />}
              </div>
            ); })}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <div className="flex gap-1">{[13, 26, 52].map((w) => <button key={w} onClick={() => setWeeks(w)} className={`rounded-md border px-2 py-1 text-xs font-medium ${weeks === w ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`} data-testid={`client-ppm-weeks-${w}`}>{w} wks</button>)}</div>
        <Pill>Read only — ask us to change a schedule</Pill>
      </div>
      {data ? <PpmGrid data={data} canEdit={false} readOnly /> : <Spinner />}
    </div>
  );
}

function Tile({ label, value, tone = "", icon: Icon, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3" data-testid={testid}>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1">{Icon && <Icon size={12} />}{label}</p>
      <p className={`text-xl font-bold font-[Chivo] ${tone}`}>{value ?? 0}</p>
    </div>
  );
}
